import AxiosInstance from '@/api/axios';
import {
  defaultPagination,
  PaginationInterface,
} from '@/components/Base/Pagination';
import { toast } from '@/components/Base/Toast/ToastManager';
import { CampaignDayInputType } from '@/components/Campaign/CampaignRules';
import { DayInputType } from '@/pages/Rule';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export interface ICampaign {
  _id: string;
  client: string | null;
  region?: string | null;
  branch?: string | null;
  name: string;
  campaign: CampaignDayInputType[];
  campaign_type?: 'standard' | 'recurring';
  automation_type?: string;
  recurring_status?: 'stopped' | 'running';
}

interface ICampaignFilter {
  client: string | null;
  branch?: string | null;
  region?: string | null;
  search?: string;
  perpage?: number;
  currentPage?: number;
}
export const fetchCampaignList = createAsyncThunk(
  'fetchCampaign/fetchData',
  async (
    { client, branch, region, currentPage, perpage, search }: ICampaignFilter,
    thunkAPI
  ) => {
    try {
      if (branch === 'all' || region === 'all')
        return thunkAPI.rejectWithValue('Invalid branch or region');
      const response = await AxiosInstance.get(
        `/re-engage/${client}/campaign`,
        {
          params: {
            branch: branch === 'others' ? '' : (branch ?? ''),
            region: region ?? '',
            currentPage,
            perpage,
            search,
          },
        }
      );
      if (response.data.success) {
        return response.data.data;
      } else {
        return [];
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createCampaign = createAsyncThunk(
  'campaign/create',
  async (
    {
      client,
      branch,
      region,
      payload,
    }: {
      client: string | null;
      branch?: string | null;
      region?: string | null;
      payload: any;
    },
    thunkAPI
  ) => {
    try {
      const response = await AxiosInstance.post(
        `/re-engage/${client}/campaign`,
        payload,
        {
          params: {
            branch: branch === 'others' ? '' : (branch ?? ''),
            region: region ?? '',
          },
        }
      );
      toast.show({
        title: 'Re-Engage campaign created successfully',
        content: 'Changes saved successfully',
        type: 'primary',
        duration: 2000,
      });
      return response.data.data;
    } catch (error) {
      toast.show({
        title: 'Failed to save Re-Engage campaign',
        content: 'Changes could not be saved',
        type: 'warning',
        duration: 2000,
      });
      console.log(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateCampaignById = createAsyncThunk(
  'campaign/update',
  async (
    {
      client,
      campaignId,
      branch,
      region,
      payload,
    }: {
      client: string | null;
      campaignId: string;
      branch?: string | null;
      region?: string | null;
      payload: any;
    },
    thunkAPI
  ) => {
    try {
      const response = await AxiosInstance.patch(
        `/re-engage/${client}/${campaignId}/campaign`,
        payload,
        {
          params: {
            branch: branch === 'others' ? '' : (branch ?? ''),
            region: region ?? '',
          },
        }
      );
      toast.show({
        title: 'Re-Engage campaign updated successfully',
        content: 'Changes saved successfully',
        type: 'primary',
        duration: 2000,
      });
      return response.data.data;
    } catch (error) {
      toast.show({
        title: 'Failed to save campaign',
        content: 'Changes could not be saved',
        type: 'warning',
        duration: 2000,
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteCampaignById = createAsyncThunk(
  'campaign/delete',
  async (
    {
      client,
      campaignId,
      branch,
      region,
    }: {
      client: string | null;
      campaignId: string;
      branch?: string | null;
      region?: string | null;
    },
    thunkAPI
  ) => {
    try {
      await AxiosInstance.delete(
        `/re-engage/${client}/${campaignId}/campaign`,
        {
          params: {
            branch: branch === 'others' ? '' : (branch ?? ''),
            region: region ?? '',
          },
        }
      );
      toast.show({
        title: 'Re-Engage campaign deleted successfully',
        content: 'Changes saved successfully',
        type: 'primary',
        duration: 2000,
      });
      return campaignId;
    } catch (error) {
      toast.show({
        title: 'Failed to delete Re-Engage campaign',
        content: 'Re-Engage campaign could not be deleted',
        type: 'warning',
        duration: 2000,
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

interface ICampaignSlice {
  campaigns: ICampaign[];
  pagination: PaginationInterface;
  loading: boolean;
  error: string | null;
}

const initialState: ICampaignSlice = {
  campaigns: [],
  pagination: defaultPagination,
  loading: false,
  error: null,
};

const campaignReducer = createSlice({
  name: 'campaign',
  initialState,
  reducers: {
    updatePagination: (state, action) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCampaignList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCampaignList.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload.items;
        state.pagination = action.payload.pagination;
      })

      .addCase(createCampaign.fulfilled, (state, action) => {
        state.campaigns.push(action.payload);
      })

      .addCase(updateCampaignById.fulfilled, (state, action) => {
        const index = state.campaigns.findIndex(
          (c) => c._id === action.payload._id
        );
        if (index !== -1) {
          state.campaigns[index] = action.payload;
        }
      })

      .addCase(deleteCampaignById.fulfilled, (state, action) => {
        state.campaigns = state.campaigns.filter(
          (c) => c._id !== action.payload
        );
      });
  },
});

export const { updatePagination } = campaignReducer.actions;

export default campaignReducer.reducer;
